import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import BackButton from "../../components/BackButton";
import { useAuth } from "../../lib/auth";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import SEO from "../../components/SEO";
import {
  Instagram, Youtube, Facebook, Music2, Plus, Sparkles, RefreshCw, Zap, Target,
  Calendar as CalIcon, Wand2, Users, Bell, TrendingUp, ShieldCheck, Trophy,
  Copy, Trash2, ArrowUp, ArrowDown, Loader2, Radio,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Area, AreaChart,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import {
  SectionHeader, GlassCard, ScoreGauge, StatPill, BadgePill, InsightBubble, formatNum,
} from "./GrowthUI";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram, tone: "hot", live: true },
  { key: "facebook", label: "Facebook", icon: Facebook, tone: "growth", live: true },
  { key: "tiktok", label: "TikTok", icon: Music2, tone: "warn", live: false },
  { key: "youtube", label: "YouTube", icon: Youtube, tone: "hot", live: false },
];

const NAV_SECTIONS = [
  { key: "overview", label: "Overview", icon: Zap },
  { key: "audit", label: "AI Audit", icon: ShieldCheck },
  { key: "content", label: "Content", icon: Sparkles },
  { key: "recommendations", label: "Growth Recs", icon: TrendingUp },
  { key: "captions", label: "Caption AI", icon: Wand2 },
  { key: "planner", label: "30-Day Plan", icon: Target },
  { key: "calendar", label: "Calendar", icon: CalIcon },
  { key: "weekly", label: "Weekly Report", icon: Radio },
  { key: "readiness", label: "Brand Ready", icon: Trophy },
  { key: "insights", label: "Audience", icon: Users },
  { key: "competitors", label: "Competitors", icon: Users },
  { key: "goals", label: "Goals", icon: Target },
  { key: "notifications", label: "Notifications", icon: Bell },
];

const CHART_COLORS = ["#FF3B6B", "#9D4EDD", "#2EC5FF", "#66D19E", "#FFB020", "#FF7A00"];

function PlatformIcon({ platform, className = "h-5 w-5" }) {
  const p = PLATFORMS.find((x) => x.key === platform);
  const Icon = p?.icon || Instagram;
  return <Icon className={className} />;
}

/* -------------------- Account connectors -------------------- */
function AccountConnectors({ accounts, onConnect, onDisconnect, connecting }) {
  const [pendingPlatform, setPendingPlatform] = useState(null);
  const [username, setUsername] = useState("");

  const isConnected = (k) => accounts.find((a) => a.platform === k);

  const handleConnect = (platform) => {
    if (!PLATFORMS.find((p) => p.key === platform)?.live) {
      toast.info("This platform is coming soon");
      return;
    }
    setPendingPlatform(platform);
  };

  const submit = async () => {
    if (!pendingPlatform) return;
    await onConnect(pendingPlatform, username);
    setPendingPlatform(null);
    setUsername("");
  };

  return (
    <div>
      <SectionHeader
        eyebrow="Step 1 · Connect"
        title="Link your social accounts"
        subtitle="Connect once and Nexus Growth AI audits your profile, generates content ideas and tracks your growth in real time."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="growth-connectors">
        {PLATFORMS.map((p) => {
          const acc = isConnected(p.key);
          const Icon = p.icon;
          return (
            <div key={p.key} className="p-5 rounded-3xl bg-[#0C0C10] hairline card-hover flex flex-col" data-testid={`connect-card-${p.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="h-11 w-11 rounded-2xl bg-white/5 flex items-center justify-center hairline">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                {!p.live ? (
                  <BadgePill label="Coming soon" tone="warn" />
                ) : acc ? (
                  <BadgePill label="Connected" tone="ready" icon={ShieldCheck} />
                ) : (
                  <BadgePill label="Not linked" />
                )}
              </div>
              <div className="font-display font-bold text-lg">{p.label}</div>
              <div className="text-xs text-zinc-400 mt-1 flex-1">
                {acc ? `@${acc.username}` : `Connect your ${p.label} to unlock AI growth insights.`}
              </div>
              {acc && acc.stats ? (
                <div className="grid grid-cols-3 gap-2 mt-3 mb-3">
                  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Followers</div><div className="font-display font-bold tabular-nums">{formatNum(acc.stats.followers)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Posts</div><div className="font-display font-bold tabular-nums">{acc.stats.posts}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Eng</div><div className="font-display font-bold tabular-nums text-emerald-300">{acc.stats.engagement_rate}%</div></div>
                </div>
              ) : null}
              {acc && !acc.coming_soon ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDisconnect(acc.id)}
                  className="bg-white/5 border-white/15 text-zinc-300 hover:bg-white/10 hover:text-white rounded-full mt-auto"
                  data-testid={`disconnect-${p.key}-btn`}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  onClick={() => handleConnect(p.key)}
                  disabled={connecting || !p.live}
                  className={p.live ? "nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 mt-auto" : "bg-white/5 border border-white/15 text-zinc-500 rounded-full mt-auto"}
                  data-testid={`connect-${p.key}-btn`}
                >
                  {p.live ? "Connect" : "Coming soon"}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {pendingPlatform && (
        <div className="mt-5 glass rounded-2xl p-5 flex items-center gap-3 flex-wrap" data-testid="connect-modal">
          <PlatformIcon platform={pendingPlatform} />
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={`@username on ${pendingPlatform}`}
            className="max-w-xs bg-white/[0.02] border-white/10 text-white"
            data-testid="connect-username-input"
          />
          <Button onClick={submit} disabled={connecting} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="confirm-connect-btn">
            {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}
          </Button>
          <Button variant="outline" onClick={() => setPendingPlatform(null)} className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white">
            Cancel
          </Button>
          <div className="text-[11px] text-zinc-500 basis-full">
            Demo mode — Nexus seeds a realistic account so every AI feature works end-to-end. Real OAuth ships once Meta/Google app review completes.
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Overview -------------------- */
function OverviewSection({ account }) {
  if (!account) return null;
  const trend = account.trend || [];
  const s = account.stats;
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <GlassCard testid="growth-account-card">
          <div className="flex flex-col items-center text-center">
            <div className="h-24 w-24 rounded-3xl overflow-hidden hairline bg-white/5 nexus-border-glow mb-3">
              <img src={account.avatar_url} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="font-display font-bold text-xl">@{account.username}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest mt-1 flex items-center gap-1.5">
              <PlatformIcon platform={account.platform} className="h-3.5 w-3.5" />
              {account.platform}
            </div>
            <div className="mt-5">
              <ScoreGauge value={account.health_score} label="Health" size={140} />
            </div>
          </div>
        </GlassCard>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill label="Followers" value={formatNum(s.followers)} />
          <StatPill label="Following" value={formatNum(s.following)} />
          <StatPill label="Posts" value={formatNum(s.posts)} />
          <StatPill label="Engagement" value={`${s.engagement_rate}%`} tone="up" />
          <StatPill label="30-day reach" value={formatNum(s.reach_last_30d)} />
          <StatPill label="Profile visits" value={formatNum(s.profile_visits_last_30d)} />
          <StatPill label="Website clicks" value={formatNum(s.website_clicks_last_30d)} />
          <StatPill label="Health" value={`${account.health_score}/100`} tone={account.health_score >= 70 ? "up" : "down"} />
        </div>
      </div>
      <GlassCard>
        <SectionHeader eyebrow="30-day trend" title="Follower growth" subtitle="Live from your connected account." />
        <div className="h-56" data-testid="growth-follower-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#9D4EDD" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#9D4EDD" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={40} />
              <YAxis tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} width={40} tickFormatter={formatNum} />
              <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="followers" stroke="#9D4EDD" strokeWidth={2} fill="url(#areaFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
    </div>
  );
}

/* -------------------- Audit -------------------- */
function AuditSection({ account, audit, loading, onRun }) {
  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="AI account audit"
        title="Your profile, analysed by AI"
        subtitle="Nexus AI grades every element of your profile and gives you specific fixes."
        action={
          <Button onClick={onRun} disabled={loading} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="run-audit-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {audit && audit.profile_score !== undefined ? "Re-run audit" : "Run audit"}
          </Button>
        }
      />
      {(!audit || audit.profile_score === undefined) && (
        <GlassCard>
          <p className="text-sm text-zinc-400">Run the audit to get a personalised profile score and fixes.</p>
        </GlassCard>
      )}
      {audit && audit.profile_score !== undefined && (
        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          <GlassCard testid="audit-score-card">
            <div className="flex flex-col items-center">
              <ScoreGauge value={audit.profile_score} label="Profile" size={200} />
              <div className="text-center mt-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Verdict</div>
                <div className="font-display font-bold mt-1">
                  {audit.profile_score >= 80 ? "Strong profile" : audit.profile_score >= 60 ? "Solid — with wins available" : "Needs work"}
                </div>
              </div>
            </div>
          </GlassCard>
          <div className="space-y-4">
            <GlassCard>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Checks</div>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(audit.checks || {}).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-xl bg-white/[0.02] hairline">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">{k.replace(/_/g, " ")}</div>
                    <div className="text-sm text-zinc-200">{v}</div>
                  </div>
                ))}
              </div>
            </GlassCard>
            <div className="space-y-2" data-testid="audit-recs-list">
              {(audit.recommendations || []).map((r, i) => (
                <InsightBubble key={i} testid={`audit-rec-${i}`}>{r}</InsightBubble>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Content performance -------------------- */
function ContentSection({ accountId }) {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/growth/content/${accountId}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const genInsights = async () => {
    try {
      const r = await api.post(`/growth/insights/${accountId}`);
      setInsights(r.data.insights || []);
      toast.success("Fresh AI insights generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <GlassCard>Loading content…</GlassCard>;
  const items = data.items || [];
  const barData = items.slice(0, 12).map((c) => ({
    name: new Date(c.posted_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    engagement: c.engagement_rate,
    type: c.type,
  }));

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Content performance"
        title="What's working, what isn't"
        action={
          <Button onClick={genInsights} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="gen-insights-btn">
            <Sparkles className="h-4 w-4 mr-2" /> Generate AI insights
          </Button>
        }
      />
      <div className="grid md:grid-cols-3 gap-4">
        <StatPill label="Avg engagement" value={`${data.avg_engagement}%`} tone="up" />
        <StatPill label="Best post" value={`${data.best?.engagement_rate}%`} hint={data.best?.type} tone="up" />
        <StatPill label="Weakest post" value={`${data.worst?.engagement_rate}%`} hint={data.worst?.type} tone="down" />
      </div>
      {insights.length > 0 && (
        <div className="grid md:grid-cols-3 gap-3">
          {insights.map((s, i) => <InsightBubble key={i}>{s}</InsightBubble>)}
        </div>
      )}
      <GlassCard>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Engagement by post</div>
        <div className="h-64" data-testid="content-bar-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData}>
              <XAxis dataKey="name" tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} width={30} />
              <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Bar dataKey="engagement" fill="#9D4EDD" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
      <GlassCard>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Recent posts</div>
        <div className="grid md:grid-cols-2 gap-3">
          {items.slice(0, 10).map((c) => (
            <div key={c.id} className="p-4 rounded-xl bg-white/[0.02] hairline flex gap-3 items-start">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-nexus-purple to-nexus-blue flex-shrink-0 flex items-center justify-center text-xs font-display font-bold uppercase">
                {c.type[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-200 truncate">{c.caption}</div>
                <div className="grid grid-cols-4 gap-2 mt-2 text-[10px] text-zinc-500 uppercase tracking-widest">
                  <span>♥ {formatNum(c.likes)}</span>
                  <span>💬 {formatNum(c.comments)}</span>
                  <span>↗ {formatNum(c.shares)}</span>
                  <span className="text-emerald-300 font-semibold">{c.engagement_rate}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}

/* -------------------- Recommendations -------------------- */
function RecsSection({ accountId }) {
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [variation, setVariation] = useState(0);

  const load = async (v = 0) => {
    setLoading(true);
    try {
      const r = await api.post(`/growth/recommendations/${accountId}`, { variation: v });
      setRecs(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [accountId]);

  const more = () => { const v = variation + 1; setVariation(v); load(v); };

  if (!recs) return <GlassCard>Loading recommendations…</GlassCard>;
  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="AI growth recommendations"
        title="Your personalised growth playbook"
        action={
          <Button onClick={more} disabled={loading} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="gen-more-ideas-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Generate more ideas
          </Button>
        }
      />
      <div className="grid md:grid-cols-2 gap-4">
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Best posting times</div>
          <div className="flex gap-2 flex-wrap">
            {recs.best_posting_times.map((t) => <BadgePill key={t} label={t} tone="growth" />)}
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-4 mb-2">Suggested posting frequency</div>
          <div className="text-sm text-zinc-200">{recs.posting_frequency}</div>
        </GlassCard>
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Hashtag suggestions</div>
          <div className="flex flex-wrap gap-1.5">
            {recs.hashtags.map((h) => <BadgePill key={h} label={h} />)}
          </div>
        </GlassCard>
      </div>
      {[
        { title: "Reel ideas", items: recs.reel_ideas, tone: "hot" },
        { title: "Carousel ideas", items: recs.carousel_ideas, tone: "growth" },
        { title: "Story ideas", items: recs.story_ideas, tone: "warn" },
        { title: "CTA suggestions", items: recs.cta_suggestions, tone: "default" },
        { title: "Caption tips", items: recs.captions, tone: "growth" },
        { title: "Trending topics", items: recs.trending_topics, tone: "hot" },
      ].map((g) => (
        <GlassCard key={g.title}>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">{g.title}</div>
          <div className="grid md:grid-cols-3 gap-3">
            {g.items.map((i, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-white/[0.02] hairline text-sm text-zinc-200">
                {i}
              </div>
            ))}
          </div>
        </GlassCard>
      ))}
    </div>
  );
}

/* -------------------- Captions -------------------- */
function CaptionSection() {
  const [form, setForm] = useState({ topic: "", niche: "", tone: "friendly" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Enter a topic"); return; }
    setLoading(true);
    try {
      const r = await api.post("/growth/captions", form);
      setResult(r.data);
      toast.success("Caption generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  return (
    <div className="space-y-6">
      <SectionHeader eyebrow="AI caption studio" title="Write scroll-stopping captions" subtitle="Enter a topic and get a hook, caption, CTA and hashtags in seconds." />
      <GlassCard>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Topic</Label>
            <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="Morning routine, gym mistakes…" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" data-testid="caption-topic-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Niche</Label>
            <Input value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} placeholder="Fitness, Fashion…" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Tone</Label>
            <select value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} className="mt-1.5 w-full h-10 bg-white/[0.02] border border-white/10 text-white rounded-md px-3 text-sm">
              {["friendly", "energetic", "professional", "cheeky", "authoritative", "storytelling"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={generate} disabled={loading} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 mt-4" data-testid="gen-caption-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
          Generate captions
        </Button>
      </GlassCard>
      {result && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="caption-result">
          {[
            { k: "Hook", v: result.hook, tone: "hot" },
            { k: "Caption", v: result.caption, tone: "growth" },
            { k: "Call to action", v: result.cta, tone: "warn" },
            { k: "Emoji version", v: result.emoji_version, tone: "hot" },
            { k: "Professional version", v: result.professional_version, tone: "growth" },
            { k: "Hashtags", v: (result.hashtags || []).join(" "), tone: "default" },
          ].map((c) => (
            <GlassCard key={c.k}>
              <div className="flex items-center justify-between mb-2">
                <BadgePill label={c.k} tone={c.tone} />
                <button onClick={() => copy(c.v)} className="text-xs text-nexus-blue hover:underline flex items-center gap-1"><Copy className="h-3 w-3" /> Copy</button>
              </div>
              <p className="text-sm text-zinc-100 whitespace-pre-wrap">{c.v}</p>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- 30-day plan -------------------- */
function PlannerSection({ accountId }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/growth/plan/${accountId}`);
      if (r.data && r.data.items) setPlan(r.data);
    } catch { /* empty */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);
  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/growth/plan/${accountId}`);
      setPlan(r.data);
      toast.success("30-day plan generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="AI content planner"
        title="A complete 30-day content plan"
        subtitle="Every day: post idea, format, caption, CTA and best time — ready to drop into your calendar."
        action={
          <Button onClick={generate} disabled={loading} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="gen-plan-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Target className="h-4 w-4 mr-2" />}
            {plan ? "Regenerate 30-day plan" : "Generate 30-day plan"}
          </Button>
        }
      />
      {plan?.items ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="plan-items">
          {plan.items.map((it) => (
            <div key={it.day} className="p-4 rounded-2xl bg-[#0C0C10] hairline card-hover">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Day {it.day}</div>
                <BadgePill label={it.content_type} tone={it.content_type === "reel" ? "hot" : it.content_type === "carousel" ? "growth" : "default"} />
              </div>
              <div className="font-display font-bold text-base mb-2">{it.title}</div>
              <p className="text-xs text-zinc-400 line-clamp-3">{it.caption}</p>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">Best time · <span className="text-nexus-blue">{it.best_time}</span></div>
            </div>
          ))}
        </div>
      ) : (
        <GlassCard><p className="text-sm text-zinc-400">Click generate to build a personalised 30-day plan from your niches.</p></GlassCard>
      )}
    </div>
  );
}

/* -------------------- Calendar -------------------- */
function CalendarSection({ accountId }) {
  const [posts, setPosts] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [newPost, setNewPost] = useState({ open: false, date: "", title: "", caption: "", content_type: "post", status: "draft" });
  const now = new Date();
  const [monthDate, setMonthDate] = useState(new Date(now.getFullYear(), now.getMonth(), 1));

  const load = async () => {
    try { const r = await api.get(`/growth/calendar/${accountId}`); setPosts(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).getDay(); // 0=Sun
  const grid = useMemo(() => {
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = new Date(Date.UTC(monthDate.getFullYear(), monthDate.getMonth(), d)).toISOString().slice(0, 10);
      cells.push({ day: d, iso, posts: posts.filter((p) => p.date === iso) });
    }
    return cells;
  }, [monthDate, daysInMonth, firstDay, posts]);

  const move = async (postId, newDate) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, date: newDate } : p)));
    try { await api.patch(`/growth/calendar/posts/${postId}`, { date: newDate }); }
    catch (e) { toast.error("Move failed"); load(); }
  };
  const cycleStatus = async (post) => {
    const next = post.status === "draft" ? "scheduled" : post.status === "scheduled" ? "published" : "draft";
    setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, status: next } : p)));
    try { await api.patch(`/growth/calendar/posts/${post.id}`, { status: next }); } catch { load(); }
  };
  const del = async (id) => {
    setPosts((prev) => prev.filter((p) => p.id !== id));
    try { await api.delete(`/growth/calendar/posts/${id}`); } catch { load(); }
  };
  const create = async () => {
    try {
      const r = await api.post(`/growth/calendar/${accountId}`, newPost);
      setPosts((prev) => [...prev, r.data]);
      setNewPost({ open: false, date: "", title: "", caption: "", content_type: "post", status: "draft" });
      toast.success("Post added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statusTone = (s) => (s === "published" ? "ready" : s === "scheduled" ? "growth" : "warn");
  const monthLabel = monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Content calendar"
        title={monthLabel}
        subtitle="Drag & drop posts between days. Click status pill to cycle Draft → Scheduled → Published."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white h-9 px-3">←</Button>
            <Button variant="outline" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white h-9 px-3">→</Button>
            <Button onClick={() => setNewPost({ ...newPost, open: true, date: new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).toISOString().slice(0, 10) })} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="add-calendar-post-btn">
              <Plus className="h-4 w-4 mr-2" /> Add post
            </Button>
          </div>
        }
      />
      <GlassCard>
        <div className="grid grid-cols-7 gap-1 mb-2 text-[10px] uppercase tracking-widest text-zinc-500 text-center">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {grid.map((cell, i) => (
            <div
              key={i}
              className="min-h-[112px] rounded-lg bg-white/[0.02] hairline p-1.5"
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (dragging && cell) move(dragging, cell.iso); setDragging(null); }}
            >
              {cell && (
                <>
                  <div className="text-[10px] text-zinc-500 mb-1">{cell.day}</div>
                  <div className="space-y-1">
                    {cell.posts.map((p) => (
                      <div
                        key={p.id}
                        draggable
                        onDragStart={() => setDragging(p.id)}
                        className="p-1.5 rounded bg-gradient-to-br from-nexus-purple/30 to-nexus-blue/30 hairline text-[10px] cursor-grab active:cursor-grabbing"
                        data-testid={`cal-post-${p.id}`}
                        title={p.caption}
                      >
                        <div className="font-semibold truncate">{p.title}</div>
                        <div className="flex items-center justify-between mt-1">
                          <button onClick={() => cycleStatus(p)} className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${p.status === "published" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : p.status === "scheduled" ? "border-nexus-blue/40 bg-nexus-blue/10 text-nexus-blue" : "border-yellow-500/40 bg-yellow-500/10 text-yellow-300"}`}>
                            {p.status}
                          </button>
                          <button onClick={() => del(p.id)} className="text-zinc-500 hover:text-nexus-pink"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </GlassCard>

      {newPost.open && (
        <GlassCard>
          <div className="grid md:grid-cols-4 gap-3">
            <Input type="date" value={newPost.date} onChange={(e) => setNewPost({ ...newPost, date: e.target.value })} className="bg-white/[0.02] border-white/10 text-white" />
            <Input placeholder="Title" value={newPost.title} onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} className="bg-white/[0.02] border-white/10 text-white" data-testid="new-post-title" />
            <select value={newPost.content_type} onChange={(e) => setNewPost({ ...newPost, content_type: e.target.value })} className="h-10 bg-white/[0.02] border border-white/10 text-white rounded-md px-3 text-sm">
              {["post", "reel", "carousel", "story"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={newPost.status} onChange={(e) => setNewPost({ ...newPost, status: e.target.value })} className="h-10 bg-white/[0.02] border border-white/10 text-white rounded-md px-3 text-sm">
              {["draft", "scheduled", "published"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <Textarea placeholder="Caption" value={newPost.caption} onChange={(e) => setNewPost({ ...newPost, caption: e.target.value })} rows={2} className="bg-white/[0.02] border-white/10 text-white mt-3" />
          <div className="flex gap-2 mt-3">
            <Button onClick={create} disabled={!newPost.date || !newPost.title} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="save-calendar-post-btn">Save</Button>
            <Button variant="outline" onClick={() => setNewPost({ ...newPost, open: false })} className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white">Cancel</Button>
          </div>
        </GlassCard>
      )}
    </div>
  );
}

/* -------------------- Weekly report -------------------- */
function WeeklySection({ accountId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/growth/weekly-report/${accountId}`).then((r) => setData(r.data)).catch(() => {}); }, [accountId]);
  if (!data) return <GlassCard>Loading…</GlassCard>;
  const chip = (v) => (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${v >= 0 ? "text-emerald-300" : "text-nexus-pink"}`}>
      {v >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />} {Math.abs(v)}%
    </span>
  );
  return (
    <div className="space-y-6">
      <SectionHeader eyebrow={`${data.period_start} → ${data.period_end}`} title="Weekly growth report" subtitle="How the last 7 days performed vs the previous week." />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatPill label="Follower growth" value={chip(data.follower_growth_pct)} />
        <StatPill label="Reach Δ" value={chip(data.reach_delta_pct)} />
        <StatPill label="Engagement Δ" value={chip(data.engagement_delta_pct)} />
        <StatPill label="Visits Δ" value={chip(data.visits_delta_pct)} />
        <StatPill label="Clicks Δ" value={chip(data.clicks_delta_pct)} />
      </div>
      <GlassCard>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Reach & engagement (7-day)</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.series}>
              <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} width={40} tickFormatter={formatNum} />
              <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Line type="monotone" dataKey="reach" stroke="#2EC5FF" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="profile_visits" stroke="#FF3B6B" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
      <div className="grid md:grid-cols-2 gap-4">
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-300 mb-2">🏆 Top content</div>
          <p className="text-sm text-zinc-200">{data.top_content?.caption}</p>
          <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-2">{data.top_content?.type} · {data.top_content?.engagement_rate}% engagement</div>
        </GlassCard>
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-nexus-pink mb-2">⚠ Weakest content</div>
          <p className="text-sm text-zinc-200">{data.weakest_content?.caption}</p>
          <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-2">{data.weakest_content?.type} · {data.weakest_content?.engagement_rate}% engagement</div>
        </GlassCard>
      </div>
    </div>
  );
}

/* -------------------- Brand readiness -------------------- */
function ReadinessSection({ accountId, onBadgesUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => { try { const r = await api.get(`/growth/brand-readiness/${accountId}`); if (r.data.score !== undefined) setData(r.data); } catch { /* empty */ } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);
  const run = async () => {
    setLoading(true);
    try { const r = await api.post(`/growth/brand-readiness/${accountId}`); setData(r.data); toast.success(`${r.data.label} · ${r.data.score}/100`); onBadgesUpdate?.(r.data.badges); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const BADGE_MAP = {
    verified_creator: { label: "Verified Creator", tone: "growth" },
    fast_growing: { label: "Fast Growing", tone: "hot" },
    high_engagement: { label: "High Engagement", tone: "ready" },
    brand_ready: { label: "Brand Ready", tone: "ready" },
  };
  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Brand readiness score"
        title="Are you ready for brand deals?"
        subtitle="A proprietary Nexus score that brands see in the Discover feed — higher scores rank you higher."
        action={
          <Button onClick={run} disabled={loading} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="compute-readiness-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trophy className="h-4 w-4 mr-2" />}
            {data ? "Re-compute" : "Compute score"}
          </Button>
        }
      />
      {data && (
        <div className="grid lg:grid-cols-[320px_1fr] gap-6">
          <GlassCard testid="readiness-score-card">
            <div className="flex flex-col items-center">
              <ScoreGauge value={data.score} label={data.label} size={220} />
              <div className="text-center mt-4">
                <div className="font-display font-bold text-lg">{data.label}</div>
                <div className="text-xs text-zinc-500 mt-1">Live on your public profile</div>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                {(data.badges || []).map((b) => (
                  <BadgePill key={b} label={BADGE_MAP[b]?.label || b} tone={BADGE_MAP[b]?.tone || "default"} icon={ShieldCheck} />
                ))}
              </div>
            </div>
          </GlassCard>
          <div className="space-y-4">
            <GlassCard>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Score breakdown</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(data.breakdown || {}).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-xl bg-white/[0.02] hairline">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{k.replace(/_/g, " ")}</div>
                    <div className="font-display font-bold text-xl tabular-nums">{v}</div>
                  </div>
                ))}
              </div>
            </GlassCard>
            <div className="space-y-2">
              {(data.recommendations || []).map((r, i) => <InsightBubble key={i}>{r}</InsightBubble>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Audience insights -------------------- */
function AudienceSection({ account }) {
  const a = account?.audience;
  if (!a) return null;
  return (
    <div className="space-y-6">
      <SectionHeader eyebrow="Audience insights" title="Who's watching" subtitle="Demographic, geo and interest breakdowns of your active audience." />
      <div className="grid md:grid-cols-2 gap-4">
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Age distribution</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={a.age}>
                <XAxis dataKey="bucket" tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} width={30} />
                <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Bar dataKey="percent" fill="#FF3B6B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Gender</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={a.gender} dataKey="percent" nameKey="label" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={2}>
                  {a.gender.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-3 flex-wrap">
            {a.gender.map((g, i) => (
              <div key={g.label} className="text-xs flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{g.label} · {g.percent}%</div>
            ))}
          </div>
        </GlassCard>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Top countries</div>
          <div className="space-y-2">
            {a.countries.map((c) => (
              <div key={c.country}>
                <div className="flex justify-between text-sm"><span className="text-zinc-200">{c.country}</span><span className="text-zinc-400">{c.percent}%</span></div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1">
                  <div className="h-full nexus-gradient rounded-full" style={{ width: `${c.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
        <GlassCard>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Top cities</div>
          <div className="space-y-2">
            {a.cities.map((c) => (
              <div key={c.city}>
                <div className="flex justify-between text-sm"><span className="text-zinc-200">{c.city}</span><span className="text-zinc-400">{c.percent}%</span></div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1">
                  <div className="h-full nexus-gradient rounded-full" style={{ width: `${c.percent * 2}%` }} />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
      <GlassCard>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Peak activity hours</div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={a.hourly_activity}>
              <defs>
                <linearGradient id="peakGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2EC5FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#2EC5FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="hour" tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} width={30} />
              <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Area type="monotone" dataKey="activity" stroke="#2EC5FF" fill="url(#peakGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
      <GlassCard>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Top interests</div>
        <div className="flex flex-wrap gap-2">
          {a.interests.map((i) => <BadgePill key={i.interest} label={`${i.interest} · ${i.score}`} tone="growth" />)}
        </div>
      </GlassCard>
    </div>
  );
}

/* -------------------- Competitors -------------------- */
function CompetitorsSection({ accountId }) {
  const [usernames, setUsernames] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get(`/growth/competitor/${accountId}`).then((r) => { if (r.data?.competitors) setData(r.data); }).catch(() => {}); }, [accountId]);
  const run = async () => {
    const arr = usernames.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 5);
    if (!arr.length) { toast.error("Enter at least one username"); return; }
    setLoading(true);
    try { const r = await api.post(`/growth/competitor/${accountId}`, { usernames: arr }); setData(r.data); toast.success("Competitor analysis ready"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-6">
      <SectionHeader eyebrow="Competitor analysis" title="Benchmark against 5 creators" subtitle="Enter up to 5 usernames — Nexus AI compares cadence, engagement and content mix." />
      <GlassCard>
        <div className="flex flex-wrap gap-3">
          <Input value={usernames} onChange={(e) => setUsernames(e.target.value)} placeholder="username1, username2, username3" className="flex-1 min-w-[240px] bg-white/[0.02] border-white/10 text-white" data-testid="competitor-input" />
          <Button onClick={run} disabled={loading} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="run-competitor-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Users className="h-4 w-4 mr-2" />}
            Analyse
          </Button>
        </div>
      </GlassCard>
      {data?.competitors && (
        <>
          <GlassCard>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Head-to-head</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="p-2">Username</th>
                    <th className="p-2">Followers</th>
                    <th className="p-2">Engagement</th>
                    <th className="p-2">Posts/wk</th>
                    <th className="p-2">Growth est.</th>
                    <th className="p-2">Content mix</th>
                  </tr>
                </thead>
                <tbody>
                  {data.competitors.map((c) => (
                    <tr key={c.username} className="border-t border-white/5" data-testid={`competitor-row-${c.username}`}>
                      <td className="p-2 font-semibold">@{c.username}</td>
                      <td className="p-2 tabular-nums">{formatNum(c.followers)}</td>
                      <td className="p-2 text-emerald-300">{c.engagement_rate}%</td>
                      <td className="p-2 tabular-nums">{c.posts_this_week}</td>
                      <td className="p-2 text-nexus-blue">+{c.follower_growth_pct_est}%</td>
                      <td className="p-2 text-xs text-zinc-400">{c.content_types.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
          <div className="space-y-2">
            {(data.recommendations || []).map((r, i) => <InsightBubble key={i}>{r}</InsightBubble>)}
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------- Goals -------------------- */
function GoalsSection({ accountId }) {
  const [goals, setGoals] = useState([]);
  const [form, setForm] = useState({ title: "", target: 10000, current: 0, metric: "followers" });
  const load = async () => { try { const r = await api.get(`/growth/goals/${accountId}`); setGoals(r.data); } catch { /* empty */ } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);
  const add = async () => {
    if (!form.title.trim()) return;
    try { const r = await api.post(`/growth/goals/${accountId}`, form); setGoals([r.data, ...goals]); setForm({ title: "", target: 10000, current: 0, metric: "followers" }); toast.success("Goal added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bump = async (g, delta) => {
    const next = Math.max(0, Math.min(g.target, g.current + delta));
    setGoals((prev) => prev.map((x) => (x.id === g.id ? { ...x, current: next } : x)));
    try { await api.patch(`/growth/goals/${g.id}`, { current: next }); } catch { load(); }
  };
  const del = async (id) => { setGoals((prev) => prev.filter((g) => g.id !== id)); try { await api.delete(`/growth/goals/${id}`); } catch { load(); } };
  return (
    <div className="space-y-6">
      <SectionHeader eyebrow="Growth goals" title="What are you chasing?" subtitle="Track big-picture goals with visual progress bars." />
      <GlassCard>
        <div className="grid md:grid-cols-5 gap-3">
          <Input placeholder="Reach 10,000 followers" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="md:col-span-2 bg-white/[0.02] border-white/10 text-white" data-testid="goal-title-input" />
          <Input type="number" placeholder="Target" value={form.target} onChange={(e) => setForm({ ...form, target: parseFloat(e.target.value) || 0 })} className="bg-white/[0.02] border-white/10 text-white" />
          <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="h-10 bg-white/[0.02] border border-white/10 text-white rounded-md px-3 text-sm">
            {["followers", "engagement", "reels_per_week", "brand_deals"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Button onClick={add} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="add-goal-btn"><Plus className="h-4 w-4 mr-2" />Add goal</Button>
        </div>
      </GlassCard>
      <div className="space-y-3">
        {goals.map((g) => {
          const pct = Math.min(100, (g.current / Math.max(g.target, 1)) * 100);
          return (
            <GlassCard key={g.id} testid={`goal-${g.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display font-bold">{g.title}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mt-0.5">{g.metric}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-xl tabular-nums">{formatNum(g.current)} / {formatNum(g.target)}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{pct.toFixed(0)}% done</div>
                </div>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden mt-3">
                <div className="h-full nexus-gradient rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => bump(g, Math.max(1, Math.round(g.target * 0.05)))} className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white h-8">+5%</Button>
                <Button size="sm" variant="outline" onClick={() => bump(g, -Math.max(1, Math.round(g.target * 0.05)))} className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white h-8">−5%</Button>
                <Button size="sm" variant="outline" onClick={() => del(g.id)} className="bg-white/5 border-white/15 rounded-full text-nexus-pink hover:bg-nexus-pink/10 hover:text-nexus-pink h-8 ml-auto"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
              </div>
            </GlassCard>
          );
        })}
        {goals.length === 0 && <GlassCard><p className="text-sm text-zinc-500">No goals yet — set your first one above.</p></GlassCard>}
      </div>
    </div>
  );
}

/* -------------------- Notifications -------------------- */
function NotificationsSection() {
  const [notifs, setNotifs] = useState([]);
  useEffect(() => { api.get("/growth/notifications").then((r) => setNotifs(r.data.notifications || [])).catch(() => {}); }, []);
  return (
    <div className="space-y-6">
      <SectionHeader eyebrow="AI notifications" title="What Nexus is watching for you" subtitle="Smart alerts based on your audience patterns and content cadence." />
      <div className="space-y-3" data-testid="growth-notifications">
        {notifs.length === 0 && <GlassCard><p className="text-sm text-zinc-500">No notifications right now. Post something and check back.</p></GlassCard>}
        {notifs.map((n) => (
          <GlassCard key={n.id} testid={`notif-${n.id}`}>
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${n.priority === "high" ? "bg-nexus-pink/15 text-nexus-pink" : "bg-nexus-blue/15 text-nexus-blue"}`}>
                <Bell className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm">{n.title}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{n.body}</div>
              </div>
              <BadgePill label={n.platform} icon={PLATFORMS.find((p) => p.key === n.platform)?.icon} />
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

/* -------------------- Root Growth AI page -------------------- */
export default function GrowthAI() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [section, setSection] = useState("overview");
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const activeAccount = accounts.find((a) => a.id === activeAccountId);
  const liveConnected = accounts.filter((a) => a.connected && !a.coming_soon);

  const loadAccounts = async () => {
    try {
      const r = await api.get("/growth/accounts");
      setAccounts(r.data);
      const live = r.data.find((a) => a.connected && !a.coming_soon);
      if (live && !activeAccountId) setActiveAccountId(live.id);
    } catch { /* empty */ }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    if (user.role !== "creator") { navigate("/brand"); return; }
    loadAccounts();
    // eslint-disable-next-line
  }, [user, loading]);

  const loadAudit = async () => {
    if (!activeAccountId) return;
    try { const r = await api.get(`/growth/audit/${activeAccountId}`); if (r.data?.profile_score !== undefined) setAudit(r.data); } catch { /* empty */ }
  };
  useEffect(() => { setAudit(null); loadAudit(); /* eslint-disable-next-line */ }, [activeAccountId]);

  const runAudit = async () => {
    setAuditLoading(true);
    try { const r = await api.post(`/growth/audit/${activeAccountId}`); setAudit(r.data); toast.success(`Profile scored ${r.data.profile_score}/100`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAuditLoading(false); }
  };

  const connect = async (platform, username) => {
    setConnecting(true);
    try {
      const r = await api.post("/growth/connect", { platform, username });
      await loadAccounts();
      if (r.data.connected && !r.data.coming_soon) setActiveAccountId(r.data.id);
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConnecting(false); }
  };

  const disconnect = async (id) => {
    try { await api.delete(`/growth/accounts/${id}`); await loadAccounts(); if (activeAccountId === id) setActiveAccountId(null); toast.success("Disconnected"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen grain">
      <SEO title="Growth AI · Creator Growth Platform" description="Nexus Growth AI — audit, plan, publish and grow your creator brand with AI." path="/growth" />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/creator" label="Back to dashboard" />
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10 pt-4 space-y-8">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">
            <Sparkles className="h-3 w-3 text-nexus-pink" /> Growth AI
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
            Grow your <span className="nexus-gradient-text">creator brand</span>, then land the deals.
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Nexus Growth AI audits your profile, plans your next 30 days, tracks your progress and pushes your
            best-scoring self into every brand's Discover feed.
          </p>
        </div>

        <AccountConnectors accounts={accounts} onConnect={connect} onDisconnect={disconnect} connecting={connecting} />

        {liveConnected.length > 0 && activeAccount && !activeAccount.coming_soon && (
          <>
            {liveConnected.length > 1 && (
              <div className="flex gap-2 flex-wrap" data-testid="account-switcher">
                {liveConnected.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setActiveAccountId(a.id)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border ${a.id === activeAccountId ? "nexus-gradient text-white border-transparent" : "bg-white/5 border-white/15 text-zinc-300 hover:bg-white/10"}`}
                  >
                    <PlatformIcon platform={a.platform} className="h-3.5 w-3.5" /> @{a.username}
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-2 flex-wrap sticky top-16 z-30 py-3 -mx-2 px-2 bg-[#060608]/80 backdrop-blur-lg border-b border-white/5" data-testid="growth-tabs">
              {NAV_SECTIONS.map((s) => {
                const Icon = s.icon;
                const active = section === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setSection(s.key)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors ${active ? "nexus-gradient text-white border-transparent" : "bg-white/5 border-white/15 text-zinc-300 hover:bg-white/10 hover:text-white"}`}
                    data-testid={`tab-${s.key}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {s.label}
                  </button>
                );
              })}
            </div>

            <div>
              {section === "overview" && <OverviewSection account={activeAccount} />}
              {section === "audit" && <AuditSection account={activeAccount} audit={audit} loading={auditLoading} onRun={runAudit} />}
              {section === "content" && <ContentSection accountId={activeAccount.id} />}
              {section === "recommendations" && <RecsSection accountId={activeAccount.id} />}
              {section === "captions" && <CaptionSection />}
              {section === "planner" && <PlannerSection accountId={activeAccount.id} />}
              {section === "calendar" && <CalendarSection accountId={activeAccount.id} />}
              {section === "weekly" && <WeeklySection accountId={activeAccount.id} />}
              {section === "readiness" && <ReadinessSection accountId={activeAccount.id} />}
              {section === "insights" && <AudienceSection account={activeAccount} />}
              {section === "competitors" && <CompetitorsSection accountId={activeAccount.id} />}
              {section === "goals" && <GoalsSection accountId={activeAccount.id} />}
              {section === "notifications" && <NotificationsSection />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
